import { Language, routeTranslations } from "@/utils/localizedRoutes";

const BASE_URL = "https://prelozim.sk";

// Organization schema - used on all pages
export function getOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`,
    "name": "Prelozim.sk - Legalizačné centrum Bratislava",
    "alternateName": ["Prelozim.sk", "Legalizačné centrum", "LC Bratislava"],
    "url": BASE_URL,
    "logo": {
      "@type": "ImageObject",
      "url": `${BASE_URL}/favicon.png`,
      "width": 512,
      "height": 512
    },
    "description": "Profesionálne úradné preklady a legalizácia dokumentov. Apostille, superlegalizácia, súdne preklady.",
    "email": "office@legalizacnecentrum.sk",
    "telephone": "+421907999909",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Pluhová 17",
      "addressLocality": "Bratislava",
      "postalCode": "831 03",
      "addressCountry": "SK"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 48.1696,
      "longitude": 17.1374
    },
    "sameAs": [
      "https://www.legalizacnecentrum.sk",
      "https://www.akmv.sk"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+421907999909",
      "contactType": "customer service",
      "availableLanguage": ["Slovak", "English", "German", "Ukrainian", "Russian", "Spanish"]
    }
  };
}

// WebSite schema with search action
export function getWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${BASE_URL}/#website`,
    "url": BASE_URL,
    "name": "Prelozim.sk",
    "description": "Úradné preklady online - rýchlo, jednoducho a profesionálne",
    "publisher": {
      "@id": `${BASE_URL}/#organization`
    },
    "inLanguage": ["sk", "en", "de", "uk", "ru", "es"]
  };
}

// LocalBusiness schema for local SEO
export function getLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${BASE_URL}/#localbusiness`,
    "name": "Prelozim.sk - Legalizačné centrum Bratislava",
    "image": `${BASE_URL}/favicon.png`,
    "url": BASE_URL,
    "telephone": "+421907999909",
    "email": "office@legalizacnecentrum.sk",
    "priceRange": "€€",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Pluhová 17",
      "addressLocality": "Bratislava",
      "postalCode": "831 03",
      "addressCountry": "SK"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 48.1696,
      "longitude": 17.1374
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00"
      }
    ],
    "sameAs": [
      "https://www.legalizacnecentrum.sk",
      "https://www.akmv.sk"
    ]
  };
}

// Breadcrumb translations
const breadcrumbNames: Record<string, Record<Language, string>> = {
  '/': { sk: 'Domov', en: 'Home', de: 'Startseite', uk: 'Головна', ru: 'Главная', es: 'Inicio' },
  '/kalkulator': { sk: 'Kalkulátor', en: 'Calculator', de: 'Rechner', uk: 'Калькулятор', ru: 'Калькулятор', es: 'Calculadora' },
  '/cennik': { sk: 'Cenník', en: 'Pricing', de: 'Preisliste', uk: 'Прайс-лист', ru: 'Прайс-лист', es: 'Precios' },
  '/o-nas': { sk: 'O nás', en: 'About Us', de: 'Über uns', uk: 'Про нас', ru: 'О нас', es: 'Sobre nosotros' },
  '/faq': { sk: 'FAQ', en: 'FAQ', de: 'FAQ', uk: 'FAQ', ru: 'FAQ', es: 'FAQ' },
  '/kontakt': { sk: 'Kontakt', en: 'Contact', de: 'Kontakt', uk: 'Контакт', ru: 'Контакт', es: 'Contacto' },
  '/ochrana-sukromia': { sk: 'Ochrana súkromia', en: 'Privacy Policy', de: 'Datenschutz', uk: 'Конфіденційність', ru: 'Конфиденциальность', es: 'Privacidad' },
  '/obchodne-podmienky': { sk: 'Obchodné podmienky', en: 'Terms', de: 'AGB', uk: 'Умови', ru: 'Условия', es: 'Términos' },
  '/sluzby/uradne-preklady': { sk: 'Úradné preklady', en: 'Certified Translations', de: 'Beglaubigte Übersetzungen', uk: 'Офіційні переклади', ru: 'Официальные переводы', es: 'Traducciones oficiales' },
  '/sluzby/legalizacia': { sk: 'Legalizácia', en: 'Legalization', de: 'Legalisierung', uk: 'Легалізація', ru: 'Легализация', es: 'Legalización' },
  '/sluzby': { sk: 'Služby', en: 'Services', de: 'Dienstleistungen', uk: 'Послуги', ru: 'Услуги', es: 'Servicios' },
};

// BreadcrumbList schema
export function getBreadcrumbSchema(skPath: string, language: Language, currentUrl: string) {
  const items: Array<{ "@type": string; position: number; name: string; item?: string }> = [];
  
  // Home is always first
  const homeUrl = language === 'sk' ? BASE_URL : `${BASE_URL}/${language}`;
  items.push({
    "@type": "ListItem",
    position: 1,
    name: breadcrumbNames['/']?.[language] || 'Home',
    item: homeUrl
  });

  // If we're not on home, add path segments
  if (skPath !== '/') {
    const segments = skPath.split('/').filter(Boolean);
    let currentPath = '';
    
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === segments.length - 1;
      
      // Build the URL for this segment
      let segmentUrl = currentPath;
      if (language !== 'sk') {
        segmentUrl = `/${language}${currentPath}`;
      }
      
      const breadcrumbItem: { "@type": string; position: number; name: string; item?: string } = {
        "@type": "ListItem",
        position: index + 2,
        name: breadcrumbNames[currentPath]?.[language] || segment.charAt(0).toUpperCase() + segment.slice(1)
      };
      
      // Only add item URL if not the last item
      if (!isLast) {
        breadcrumbItem.item = `${BASE_URL}${segmentUrl}`;
      }
      
      items.push(breadcrumbItem);
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items
  };
}

// Service schema for service pages
export function getServiceSchema(
  serviceName: string,
  description: string,
  priceRange: string,
  serviceUrl: string
) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": serviceName,
    "provider": {
      "@id": `${BASE_URL}/#organization`
    },
    "description": description,
    "url": serviceUrl,
    "areaServed": {
      "@type": "Country",
      "name": "Slovakia"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": serviceName,
      "itemListElement": [
        {
          "@type": "Offer",
          "priceSpecification": {
            "@type": "PriceSpecification",
            "priceCurrency": "EUR",
            "price": priceRange
          }
        }
      ]
    }
  };
}

// Combined schema for homepage
export function getHomePageSchemas() {
  return [
    getOrganizationSchema(),
    getWebSiteSchema(),
    getLocalBusinessSchema()
  ];
}

// PriceSpecification schema for pricing page
export function getPriceSpecificationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "PriceSpecification",
    "priceCurrency": "EUR",
    "price": "30",
    "description": "Official translation price per standard page (1800 characters)",
    "unitText": "page",
    "validFrom": "2024-01-01",
    "eligibleRegion": {
      "@type": "Country",
      "name": "Slovakia"
    }
  };
}

// Product schema for translation service with full Merchant listing fields
export function getProductSchema(reviews?: Array<{
  author: string;
  content: string;
  rating: number;
}>) {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Úradný preklad",
    "description": "Profesionálny úradný preklad overený súdnym prekladateľom s pečiatkou a podpisom. Platný pre všetky úrady, súdy a inštitúcie.",
    "image": [
      `${BASE_URL}/images/og-image.png`,
      `${BASE_URL}/favicon.png`
    ],
    "sku": "URADNY-PREKLAD-SK",
    "brand": {
      "@type": "Brand",
      "name": "Prelozim.sk"
    },
    "offers": {
      "@type": "Offer",
      "url": `${BASE_URL}/sluzby/uradne-preklady`,
      "priceCurrency": "EUR",
      "price": "30",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Prelozim.sk"
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "applicableCountry": "SK",
        "returnPolicyCategory": "https://schema.org/MerchantReturnNotPermitted",
        "merchantReturnDays": 0
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "EUR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "SK"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 3,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 2,
            "unitCode": "DAY"
          }
        }
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": "180",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  // Add reviews if available
  if (reviews && reviews.length > 0) {
    schema.review = reviews.slice(0, 5).map(r => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": r.author
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": r.rating.toString(),
        "bestRating": "5"
      },
      "reviewBody": r.content
    }));
  }

  return schema;
}

// Helper to get localized URL without trailing slash
function getLocalizedUrl(skPath: string, language: Language): string {
  const translations = routeTranslations[skPath];
  const localizedPath = translations?.[language] || skPath;
  
  if (language === 'sk') {
    return localizedPath === '/' ? BASE_URL : `${BASE_URL}${localizedPath}`;
  }
  return localizedPath === '/' 
    ? `${BASE_URL}/${language}` 
    : `${BASE_URL}/${language}${localizedPath}`;
}

// AboutPage schema
export function getAboutPageSchema(language: Language) {
  const descriptions: Record<Language, string> = {
    sk: "Spoznajte tím profesionálnych súdnych prekladateľov Prelozim.sk. 11 rokov skúseností, 180+ pozitívnych hodnotení.",
    en: "Meet the professional court translators team at Prelozim.sk. 11 years of experience, 180+ positive reviews.",
    de: "Lernen Sie das professionelle Gerichtsübersetzer-Team von Prelozim.sk kennen. 11 Jahre Erfahrung, 180+ positive Bewertungen.",
    uk: "Познайомтеся з командою професійних судових перекладачів Prelozim.sk. 11 років досвіду, 180+ позитивних відгуків.",
    ru: "Познакомьтесь с командой профессиональных судебных переводчиков Prelozim.sk. 11 лет опыта, 180+ положительных отзывов.",
    es: "Conozca al equipo de traductores jurados profesionales de Prelozim.sk. 11 años de experiencia, 180+ reseñas positivas."
  };

  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "O nás - Prelozim.sk",
    "description": descriptions[language] || descriptions.sk,
    "url": getLocalizedUrl('/o-nas', language),
    "mainEntity": {
      "@id": `${BASE_URL}/#organization`
    }
  };
}

// ContactPage schema  
export function getContactPageSchema(language: Language) {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Kontakt - Prelozim.sk",
    "description": "Kontaktujte nás pre úradné preklady a legalizáciu dokumentov.",
    "url": getLocalizedUrl('/kontakt', language),
    "mainEntity": {
      "@id": `${BASE_URL}/#organization`
    }
  };
}

// FAQPage schema
export function getFAQPageSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

// WebPage schema (generic)
export function getWebPageSchema(
  name: string,
  description: string,
  url: string,
  pageType: string = "WebPage"
) {
  return {
    "@context": "https://schema.org",
    "@type": pageType,
    "name": name,
    "description": description,
    "url": url,
    "isPartOf": {
      "@id": `${BASE_URL}/#website`
    },
    "publisher": {
      "@id": `${BASE_URL}/#organization`
    }
  };
}

// ProfessionalService schema for service pages
export function getProfessionalServiceSchema(
  serviceName: string,
  description: string,
  serviceUrl: string,
  priceFrom: string
) {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": serviceName,
    "description": description,
    "url": serviceUrl,
    "provider": {
      "@id": `${BASE_URL}/#organization`
    },
    "areaServed": [
      { "@type": "Country", "name": "Slovakia" },
      { "@type": "Country", "name": "Czech Republic" },
      { "@type": "Country", "name": "Austria" },
      { "@type": "Country", "name": "Germany" }
    ],
    "priceRange": `od ${priceFrom}€`,
    "paymentAccepted": ["Cash", "Bank Transfer", "Invoice"],
    "currenciesAccepted": "EUR"
  };
}

// SoftwareApplication schema for calculator
export function getCalculatorSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulátor ceny prekladu - Prelozim.sk",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "description": "Online kalkulátor pre výpočet ceny úradného prekladu. Nahrajte dokument a okamžite zistíte presnú cenu.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "publisher": {
      "@id": `${BASE_URL}/#organization`
    }
  };
}

// Generate JSON-LD script content
export function generateJsonLd(schemas: object | object[]): string {
  if (Array.isArray(schemas)) {
    return JSON.stringify(schemas.map(schema => schema));
  }
  return JSON.stringify(schemas);
}
